/**
 * declareJsonColumnDialogStore — global open/close state for the
 * Declare-as-JSON dialog when it's launched from outside a filter chip
 * (grid header context menu, cell context menu). The dialog mounts once at
 * the app level via `GlobalDeclareJsonColumnDialog`; any feature can pop it
 * by calling `openDeclareJsonColumnDialog(…)` without prop-drilling.
 */

import { create } from 'zustand';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

export type DeclareJsonColumnDialogContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  /** Engine-specific column data type string (e.g. `nvarchar(max)`). */
  dataType?: string;
  engine: DatabaseEngineId;
  /** Loaded column values for the row sample, used to infer JSON structure. */
  sampleValues?: ReadonlyArray<unknown>;
  /** Source label for analytics (`'header-menu'`, `'cell-menu'`, …). */
  source: string;
  /**
   * Which copy/flow variant to render.
   *  - `'eligible'` (default): long-text column being declared as JSON; the
   *     dialog validates rows and may surface PG-version gates.
   *  - `'native'`: column is already a native JSON type; the dialog
   *     skips validation copy and frames the action as "save JSON path
   *     metadata + loose-FK targets".
   * Callers that omit this field get `'eligible'` (back-compat with the
   * filter-chip / context-menu paths that predate this discriminator).
   */
  mode?: 'eligible' | 'native';
};

type DeclareJsonColumnDialogState = {
  isOpen: boolean;
  context: DeclareJsonColumnDialogContext | null;
};

type DeclareJsonColumnDialogActions = {
  openDialog: (ctx: DeclareJsonColumnDialogContext) => void;
  closeDialog: () => void;
};

type DeclareJsonColumnDialogStore = DeclareJsonColumnDialogState &
  DeclareJsonColumnDialogActions;

export const useDeclareJsonColumnDialogStore = create<DeclareJsonColumnDialogStore>(
  (set) => ({
    isOpen: false,
    context: null,
    openDialog: (ctx) => {
      // Defer one frame so an in-flight Radix ContextMenu (or other overlay)
      // close finishes BEFORE this Dialog mounts. Mounting a Radix Dialog
      // mid-close on the menu leaves `body { pointer-events: none }` stuck
      // because two overlay layers race their pointer-events locks.
      const open = () => set({ isOpen: true, context: ctx });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    },
    closeDialog: () => set({ isOpen: false }),
  }),
);

export const openDeclareJsonColumnDialog = (
  ctx: DeclareJsonColumnDialogContext,
) => useDeclareJsonColumnDialogStore.getState().openDialog(ctx);
